import { useEffect, useMemo, useRef } from "react";
import { C, FONT, clamp, hexA } from "../theme";
import type { Zone } from "../types";

/* viewBox is fixed at 360×168; the SVG scales to width 100% / height 190. */
const VB_W = 360;
const VB_H = 168;
const PAD_L = 30; // room for the y axis ticks (mono)
const PAD_R = 8;
const PAD_T = 18; // room for the "modeled" marker
const PAD_B = 28; // room for the x axis (time) labels
const PLOT_W = VB_W - PAD_L - PAD_R;
const PLOT_H = VB_H - PAD_T - PAD_B;

/* Y domain is fixed 0..100 (% plant-available water). */
const yToPx = (v: number) => PAD_T + (1 - clamp(v, 0, 100) / 100) * PLOT_H;
const xToPx = (i: number, n: number) => PAD_L + (n <= 1 ? 0 : (i / (n - 1)) * PLOT_W);

/** Soil-moisture 24h trend for one zone — bespoke SVG, draws on mount. */
export function MoistureTrend({ z }: { z: Zone }) {
  // 24 synthetic hourly points: start a bit higher, drift down ~0.5/h, bump +9 around hour 14.
  const points = useMemo(() => {
    const pts: number[] = [];
    let m = z.moisture + 6;
    for (let i = 0; i < 24; i++) {
      m -= 0.5;
      if (i === 14) m += 9;
      pts.push(clamp(m, 8, 100));
    }
    return pts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [z.id]);

  const n = points.length;
  const linePath = useMemo(
    () =>
      points
        .map((v, i) => `${i === 0 ? "M" : "L"}${xToPx(i, n).toFixed(2)} ${yToPx(v).toFixed(2)}`)
        .join(" "),
    [points, n],
  );
  const areaPath = useMemo(
    () =>
      `${linePath} L${xToPx(n - 1, n).toFixed(2)} ${(PAD_T + PLOT_H).toFixed(2)}` +
      ` L${xToPx(0, n).toFixed(2)} ${(PAD_T + PLOT_H).toFixed(2)} Z`,
    [linePath, n],
  );

  const bandTop = yToPx(z.band[1]);
  const bandBot = yToPx(z.band[0]);
  const madY = yToPx(z.mad);

  // Draw the line on mount via strokeDashoffset 1→0 (pathLength normalized to 1).
  const lineRef = useRef<SVGPathElement>(null);
  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;
    el.style.transition = "none";
    el.style.strokeDashoffset = "1";
    // force reflow so the 1→0 transition actually plays
    void el.getBoundingClientRect();
    el.style.transition = "stroke-dashoffset 1.5s cubic-bezier(.2,.8,.25,1) .2s";
    el.style.strokeDashoffset = "0";
  }, [linePath]);

  const ticks = [0, 25, 50, 75, 100];
  const gradId = `mt-fill-${z.id}`;

  // x-axis time ticks: oldest sample on the left (-24h) through "now" on the right.
  const xBaseY = PAD_T + PLOT_H;
  const xTicks: { x: number; label: string; anchor: "start" | "middle" | "end" }[] = [
    { x: xToPx(0, n), label: "−24h", anchor: "start" },
    { x: xToPx((n - 1) / 2, n), label: "−12h", anchor: "middle" },
    { x: xToPx(n - 1, n), label: "now", anchor: "end" },
  ];
  const nowX = xToPx(n - 1, n);
  const nowY = yToPx(points[n - 1]);

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="none"
      width="100%"
      height={190}
      style={{ display: "block" }}
      role="img"
      aria-label={`Modeled soil moisture over the last 24 hours for ${z.name} (simulated, not measured)`}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(C.ice, 0.22)} />
          <stop offset="100%" stopColor={hexA(C.ice, 0)} />
        </linearGradient>
      </defs>

      {/* faint horizontal grid */}
      {ticks.map((t) => (
        <line
          key={`g${t}`}
          x1={PAD_L}
          x2={VB_W - PAD_R}
          y1={yToPx(t)}
          y2={yToPx(t)}
          stroke={C.lineSoft}
          strokeWidth={1}
        />
      ))}

      {/* y axis ticks (mono) */}
      {ticks.map((t) => (
        <text
          key={`t${t}`}
          x={PAD_L - 6}
          y={yToPx(t) + 3}
          textAnchor="end"
          style={{ fontFamily: FONT.mono, fontSize: 9, fill: C.textLow }}
        >
          {t}
        </text>
      ))}

      {/* target band */}
      <rect
        x={PAD_L}
        y={bandTop}
        width={PLOT_W}
        height={Math.max(0, bandBot - bandTop)}
        fill={hexA(C.sage, 0.12)}
      />

      {/* refill (MAD) line */}
      <line
        x1={PAD_L}
        x2={VB_W - PAD_R}
        y1={madY}
        y2={madY}
        stroke={C.beacon}
        strokeWidth={1}
        strokeDasharray="2 3"
        opacity={0.8}
      />

      {/* area fill under the line */}
      <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />

      {/* moisture line — draws on mount */}
      <path
        ref={lineRef}
        d={linePath}
        fill="none"
        stroke={C.sea}
        strokeWidth={2.2}
        strokeLinejoin="round"
        strokeLinecap="round"
        pathLength={1}
        strokeDasharray={1}
        strokeDashoffset={1}
      />

      {/* "now" marker — vertical guide + dot at the latest sample, right edge */}
      <line
        x1={nowX}
        x2={nowX}
        y1={PAD_T}
        y2={xBaseY}
        stroke={C.sea}
        strokeWidth={1}
        strokeDasharray="1 3"
        opacity={0.5}
      />
      <circle cx={nowX} cy={nowY} r={2.8} fill={C.sea} stroke={C.paper} strokeWidth={1.2} />

      {/* x axis (time) ticks + labels */}
      {xTicks.map((t) => (
        <text
          key={`x${t.label}`}
          x={t.x}
          y={xBaseY + 14}
          textAnchor={t.anchor}
          style={{ fontFamily: FONT.mono, fontSize: 9, fill: C.textLow }}
        >
          {t.label}
        </text>
      ))}

      {/* "modeled" honesty marker — top-left corner, muted */}
      <text
        x={PAD_L}
        y={11}
        textAnchor="start"
        style={{
          fontFamily: FONT.mono,
          fontSize: 8.5,
          letterSpacing: 0.6,
          fill: C.muted,
          textTransform: "uppercase",
        }}
      >
        modeled · not measured
      </text>

      {/* inline legend — the two reference marks that drive the decision.
          Laid out left→right and right-anchored to the plot's right edge:
          [band swatch] target   [dashed line] MAD */}
      <g
        transform={`translate(${VB_W - PAD_R - 96}, 11)`}
        style={{ fontFamily: FONT.mono, fontSize: 8.5 }}
      >
        {/* target band swatch */}
        <rect x={0} y={-5} width={9} height={6} rx={1} fill={hexA(C.sage, 0.4)} />
        <text x={12} y={0} fill={C.muted}>
          target
        </text>
        {/* refill (MAD) dashed line */}
        <line
          x1={52}
          x2={62}
          y1={-2}
          y2={-2}
          stroke={C.beacon}
          strokeWidth={1.2}
          strokeDasharray="2 2"
        />
        <text x={65} y={0} fill={C.muted}>
          MAD
        </text>
      </g>
    </svg>
  );
}
